<template>
    <main v-if="data" class="pt-12">
        <ContentRenderer :value="data">
            <section
                class="mx-6 lg:mx-12 3xl:mx-0 relative md:sticky md:top-10 md:mt-14 mb-12 z-0 transform-gpu"
            >
                <div class="bg-fjell rounded-t-2xl mx-auto max-w-[1200px] relative">
                    <figure
                        class="absolute top-full -mt-2 -left-4 -right-4 lg:-left-[30px] lg:-right-[30px]"
                    >
                        <img src="~/assets/svg/intro/tavle-bunn.svg" class="w-full" />
                    </figure>

                    <img
                        src="~/assets/svg/intro/la-staa.svg"
                        class="absolute bottom-8 right-12 w-[200px] lg:w-auto"
                    />
                    <img
                        src="~/assets/svg/intro/regnestykke.svg"
                        class="absolute left-[80%] top-[10%] md:left-32 md:top-20 w-[10%]"
                    />
                    <div
                        class="relative pl-16 pr-6 md:px-0 md:left-[35%] md:w-3/5 py-8 pb-32 md:pt-28"
                    >
                        <h2 class="lg:font-heading1 relative whitespace-pre-wrap">
                            <svg-icon
                                name="krusedull"
                                class="w-5 h-5 absolute right-full mr-5 top-4"
                            />{{ data?.top.title }}
                        </h2>
                        <p class="lg:font-lead my-8 w-4/5 whitespace-pre-wrap">
                            {{ data?.top.intro }}
                        </p>
                        <a
                            target="_blank"
                            :href="`https://assets.mustasj.dev/pengemester/manus-${locale}.pdf`"
                            class="btn bg-nordlys text-white"
                        >
                            {{ data?.top.link }}
                        </a>
                    </div>
                </div>
            </section>
            <figure
                class="mx-auto max-w-[1600px] relative lg:sticky top-4 md:top-24 z-[2] transform-gpu pointer-events-none"
            >
                <img
                    src="~/assets/svg/intro/staa-ved-mynter.svg"
                    class="ml-6 md:ml-24 xl:ml-18 2xl:ml-32 w-2/5 md:w-1/5 -mb-[35%] md:-mb-[20%] -mt-[20%] md:-mt-[20%]"
                />
            </figure>
            <section class="pt-[20%] 2xl:pt-[500px] relative z-[1] transform-gpu overflow-hidden">
                <div
                    class="absolute bg-frost top-0 h-[200%] w-[150%] left-1/2 -translate-x-1/2 rounded-[100%]"
                ></div>
            </section>

            <!-- 0. Introduksjon og innlogging -->
            <div class="relative z-[3] md:-mt-[10%] 2xl:-mt-[300px] transform-gpu">
                <section class="relative bg-frost-30 pb-12 lg:pb-32 overflow-hidden">
                    <div
                        class="absolute bg-frost bottom-0 h-[200%] w-[500%] lg:w-[300%] xl:w-[170%] left-1/2 -translate-x-1/2 rounded-[100%]"
                    ></div>
                    <div
                        class="relative z-10 flex flex-col lg:flex-row gap-6 md:gap-10 text-fjell pb-6 md:pb-16 px-4 lg:px-12 2xl:px-24 mx-auto max-w-[1600px] justify-between pt-0"
                    >
                        <div class="flex flex-col gap-6 md:gap-10 lg:w-2/3">
                            <div
                                class="bg-white shadow-xl rounded-xl p-10 md:py-16 flex flex-col md:flex-row gap-8"
                            >
                                <figure class="md:w-1/3 max-w-[300px] flex-none">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/forside/fjernkontroll.png"
                                        loading="lazy"
                                        class="w-full"
                                    />
                                </figure>
                                <div class="space-y-6 font-sublead">
                                    <h3>{{ data?.section1.box1.title }}</h3>
                                    <p v-for="text in data?.section1.box1.texts" v-html="text"></p>
                                </div>
                            </div>
                            <div
                                class="bg-white shadow-xl rounded-xl p-10 md:py-16 flex flex-col md:flex-row gap-8"
                            >
                                <figure class="md:w-1/3 max-w-[300px] flex-none">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/forside/fjernkontroll-2.png"
                                        loading="lazy"
                                        class="w-full"
                                    />
                                </figure>
                                <div class="space-y-6 font-sublead">
                                    <h3>{{ data?.section1.box2.title }}</h3>
                                    <p v-for="text in data?.section1.box2.texts" v-html="text"></p>
                                </div>
                            </div>
                        </div>
                        <div class="bg-white shadow-xl rounded-xl p-10 lg:w-1/3">
                            <Lottie
                                src="/lottie/highfive.json"
                                :loop="true"
                                :width="400"
                                :height="400"
                            />
                            <h3>{{ data?.section1.box3.title }}</h3>
                            <dl class="flex flex-wrap mt-4">
                                <template v-for="info in data?.section1.box3.infos">
                                    <dt class="font-bold w-2/5 my-2">{{ info.key }}</dt>
                                    <dd class="w-3/5 my-2 whitespace-pre-wrap">
                                        {{ info.value }}
                                    </dd>
                                </template>
                            </dl>
                        </div>
                    </div>
                </section>
                <section class="relative bg-frost-30">
                    <div
                        class="relative z-10 flex flex-col md:flex-row gap-12 py-6 pt-12 md:py-16 lg:pt-32 px-4 lg:px-12 2xl:px-24 mx-auto max-w-[1600px] justify-between"
                    >
                        <div class="relative md:w-3/5">
                            <div
                                class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                            >
                                <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                    {{ data?.page0.title }}
                                </h3>
                                <div class="text-vann flex items-center gap-2">
                                    <svg-icon name="tid" class="w-6 h-6" />
                                    <span>{{ data?.page0.readtime }}</span>
                                </div>
                                <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                    <p>
                                        {{ data?.page0.text1 }}
                                    </p>
                                    <ul class="list-disc space-y-2 pl-4">
                                        <li v-for="text in data?.page0.list">
                                            {{ text }}
                                        </li>
                                    </ul>
                                    <p>{{ data?.page0.text2 }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                            <figure class="w-full">
                                <NuxtImg
                                    format="webp"
                                    src="/img/screens/00a.png"
                                    loading="lazy"
                                    width="544"
                                    placeholder
                                    class="mb-2 w-full rounded-xl"
                                />
                                <figcaption>{{ data?.page0.figcaption1 }}</figcaption>
                            </figure>
                            <div class="flex gap-10">
                                <figure class="w-1/2">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/00a-mobil.png"
                                        width="272"
                                        loading="lazy"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page0.figcaption2 }}</figcaption>
                                </figure>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 0. intro -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost-30 rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >0</span
                                        >
                                        {{ data?.page1.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page1.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="video" class="w-6 h-6" />
                                            <span>{{ data?.page1.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p class="font-heading6">{{ data?.page1.intro }}</p>

                                        <blockquote
                                            class="space-y-4 md:border-l-3 md:border-frost md:pl-6 italic !my-8"
                                        >
                                            <p v-for="text in data?.page1.texts" v-html="text"></p>
                                            <strong class="inline-block">
                                                {{ data?.page1.quoteEnd }}
                                            </strong>
                                        </blockquote>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <VideoInline
                                        class="mb-2"
                                        src="https://assets.mustasj.dev/pengemester/media/film0.mp4"
                                        poster="/img/screens/01a.png"
                                        :tracks="[
                                            {
                                                kind: 'subtitles',
                                                src: 'https://assets.mustasj.dev/pengemester/media/film0.vtt',
                                                srcLang: 'no',
                                                label: 'Bokmål',
                                                default: true
                                            }
                                        ]"
                                    />
                                    <figcaption>{{ data?.page1.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/01a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page1.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 1. Pengebruk -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >1</span
                                        >
                                        {{ data?.page2.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page2.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page2.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page2.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page2.discussion.title }}</strong>
                                            <p>{{ data?.page2.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/02a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page2.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/02a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page2.figcaption2 }}</figcaption>
                                    </figure>
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/02b-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page2.figcaption3 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 2. Inntekt -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >2</span
                                        >
                                        {{ data?.page3.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page3.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page3.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page3.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page3.discussion.title }}</strong>
                                            <p>{{ data?.page3.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/03a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page3.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/03a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page3.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 3. smart pengebruk -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >3</span
                                        >
                                        {{ data?.page4.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page4.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="video" class="w-6 h-6" />
                                            <span>{{ data?.page4.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p class="font-heading6">{{ data?.page4.intro }}</p>

                                        <blockquote
                                            class="space-y-4 md:border-l-3 md:border-frost md:pl-6 italic !my-8"
                                        >
                                            <p v-for="text in data?.page4.texts" v-html="text"></p>
                                            <strong class="inline-block">
                                                {{ data?.page4.quoteEnd }}
                                            </strong>
                                        </blockquote>
                                        <p>
                                            {{ data?.page4.lastText }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <VideoInline
                                        class="mb-2"
                                        src="https://assets.mustasj.dev/pengemester/media/film1.mp4"
                                        poster="/img/screens/04a.png"
                                        :tracks="[
                                            {
                                                kind: 'subtitles',
                                                src: 'https://assets.mustasj.dev/pengemester/media/film1.vtt',
                                                srcLang: 'no',
                                                label: 'Norsk',
                                                default: true
                                            }
                                        ]"
                                    />
                                    <figcaption>{{ data?.page4.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/04a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page4.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 4. Sparemål -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >4</span
                                        >
                                        {{ data?.page5.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page5.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page5.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page5.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page5.discussion.title }}</strong>
                                            <p>{{ data?.page5.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/05a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page5.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/05a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page5.figcaption2 }}</figcaption>
                                    </figure>
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/05b-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page5.figcaption3 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 5. Sparing -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >5</span
                                        >
                                        {{ data?.page6.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page6.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="video" class="w-6 h-6" />
                                            <span>{{ data?.page6.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p class="font-heading6">
                                            {{ data?.page6.intro }}
                                        </p>

                                        <blockquote
                                            class="space-y-4 md:border-l-3 md:border-frost md:pl-6 italic !my-8"
                                        >
                                            <p v-for="text in data?.page6.texts" v-html="text"></p>
                                            <strong class="inline-block">
                                                {{ data?.page6.quoteEnd }}
                                            </strong>
                                        </blockquote>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <VideoInline
                                        class="mb-2"
                                        src="https://assets.mustasj.dev/pengemester/media/film2.mp4"
                                        poster="/img/screens/06a.png"
                                        :tracks="[
                                            {
                                                kind: 'subtitles',
                                                src: 'https://assets.mustasj.dev/pengemester/media/film2.vtt',
                                                srcLang: 'no',
                                                label: 'Norsk',
                                                default: true
                                            }
                                        ]"
                                    />
                                    <figcaption>{{ data?.page6.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/06a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page6.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 6. Justere forbruk -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >6</span
                                        >
                                        {{ data?.page7.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page7.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page7.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page7.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page7.discussion.title }}</strong>
                                            <p>{{ data?.page7.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/07a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page7.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/07a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page7.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 7. Ett år frem i tid -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >7</span
                                        >
                                        {{ data?.page8.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page8.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page8.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page8.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page8.discussion.title }}</strong>
                                            <p>{{ data?.page8.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/08a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page8.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/08a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page8.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 8. Knust telefon -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >8</span
                                        >
                                        {{ data?.page9.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page9.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page9.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page9.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page9.discussion.title }}</strong>
                                            <p>{{ data?.page9.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/09a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page9.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/09a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page9.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 9. Betaling -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >9</span
                                        >
                                        {{ data?.page10.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page10.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page10.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page10.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page10.discussion.title }}</strong>
                                            <p>{{ data?.page10.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/10a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page10.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/10a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page10.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 10. Renter -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >10</span
                                        >
                                        {{ data?.page11.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page11.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="video" class="w-6 h-6" />
                                            <span>{{ data?.page11.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p class="font-heading6">
                                            {{ data?.page11.intro }}
                                        </p>

                                        <blockquote
                                            class="space-y-4 md:border-l-3 md:border-frost md:pl-6 italic !my-8"
                                        >
                                            <p v-for="text in data?.page11.texts" v-html="text"></p>
                                            <strong class="inline-block">
                                                {{ data?.page11.quoteEnd }}
                                            </strong>
                                        </blockquote>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <VideoInline
                                        class="mb-2"
                                        src="https://assets.mustasj.dev/pengemester/media/film3.mp4"
                                        poster="/img/screens/11a.png"
                                        :tracks="[
                                            {
                                                kind: 'subtitles',
                                                src: 'https://assets.mustasj.dev/pengemester/media/film3.vtt',
                                                srcLang: 'no',
                                                label: 'Norsk',
                                                default: true
                                            }
                                        ]"
                                    />
                                    <figcaption>{{ data?.page11.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/11a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page11.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 11. Justering av forbruk – andre runde -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >11</span
                                        >
                                        {{ data?.page12.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page12.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page12.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page12.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page12.discussion.title }}</strong>
                                            <p>{{ data?.page12.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/12a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page12.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/12a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page12.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 12. Nytt år frem i tid -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >12</span
                                        >
                                        {{ data?.page13.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page13.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page13.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page13.texts" v-html="text"></p>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/13a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page13.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/13a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page13.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 13. JHva kostet telefonen egentlig? -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >13</span
                                        >
                                        {{ data?.page14.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page14.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page14.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page14.texts" v-html="text"></p>

                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page14.discussion.title }}</strong>
                                            <p>{{ data?.page14.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/14a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page14.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/14a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page14.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 14. Sparemetoder -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >14</span
                                        >
                                        {{ data?.page15.title }}
                                    </h3>
                                    <div class="text-vann flex items-center gap-6">
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="tid" class="w-6 h-6" />
                                            <span>{{ data?.page15.readtime }}</span>
                                        </div>
                                        <div class="inline-flex gap-2">
                                            <svg-icon name="task-add" class="w-6 h-6" />
                                            <span>{{ data?.page15.type }}</span>
                                        </div>
                                    </div>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page15.texts" v-html="text"></p>
                                        <div class="bg-sand-30 rounded p-4 md:p-8 space-y-2">
                                            <strong>{{ data?.page15.discussion.title }}</strong>
                                            <p>{{ data?.page15.discussion.text }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/15a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />

                                    <figcaption>{{ data?.page15.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/15a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page15.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 15. Noe dere ikke skjønte? -->
                <section class="bg-frost">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-natt rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >15</span
                                        >
                                        {{ data?.page16.title }}
                                    </h3>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page16.texts" v-html="text"></p>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10 text-fjell">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/16a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page16.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/16a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page16.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- 16. Takk for at du spilte! -->
                <section class="bg-natt">
                    <div
                        class="relative py-6 pt-12 px-4 md:py-16 lg:px-12 2xl:px-24 mx-auto max-w-[1600px]"
                    >
                        <span
                            class="absolute -top-2 left-1/2 md:left-24 lg:left-32 2xl:left-[11.5rem] w-10 h-10 bg-frost rounded rotate-45 -translate-y-1/2 -translate-x-1/2 md:translate-x-0"
                        ></span>
                        <div class="relative z-10 flex flex-col md:flex-row gap-12 justify-between">
                            <div class="relative md:w-3/5">
                                <div
                                    class="bg-white text-fjell shadow-xl p-6 py-8 sm:p-8 md:p-10 lg:p-12 2xl:p-20 rounded-xl"
                                >
                                    <h3 class="font-heading2 flex items-center gap-3 mb-4">
                                        <span
                                            class="css--page-number inline-block !bg-fjell !mb-0 flex-none"
                                            >16</span
                                        >
                                        {{ data?.page17.title }}
                                    </h3>
                                    <div class="space-y-4 mt-6 md:mt-12 max-w-[700px]">
                                        <p v-for="text in data?.page16.texts" v-html="text"></p>
                                    </div>
                                </div>
                            </div>
                            <div class="relative md:w-2/5 flex flex-col gap-10">
                                <figure class="w-full">
                                    <NuxtImg
                                        format="webp"
                                        src="/img/screens/17a.png"
                                        loading="lazy"
                                        width="544"
                                        placeholder
                                        class="mb-2 w-full rounded-xl"
                                    />
                                    <figcaption>{{ data?.page17.figcaption1 }}</figcaption>
                                </figure>
                                <div class="flex gap-10">
                                    <figure class="w-1/2">
                                        <NuxtImg
                                            format="webp"
                                            src="/img/screens/17a-mobil.png"
                                            loading="lazy"
                                            width="272"
                                            placeholder
                                            class="mb-2 w-full rounded-xl"
                                        />
                                        <figcaption>{{ data?.page17.figcaption2 }}</figcaption>
                                    </figure>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </ContentRenderer>
    </main>
</template>

<script setup lang="ts">
useSeoMeta({
    title: 'Les gjennom manuset',
    description:
        'Les gjennom hele manuset for Pengesmart-undervisningen – et interaktivt opplegg om privatøkonomi for ungdomsskole og videregående.',
    ogImage: '/img/toppbilde-seo-1200x630.png',
    twitterCard: 'summary_large_image'
});
const { locale } = useI18n();
const { data } = await useAsyncData('data', () => queryContent(`${locale.value}/manus`).findOne());
</script>
